import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

export default function Volunteer() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    program: '',
    availability: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const programs = [
    { value: 'food', label: 'Food Aid Program' },
    { value: 'education', label: 'Education Support' },
    { value: 'disaster', label: 'Disaster Relief' },
    { value: 'pets', label: 'Pet Rescue' },
  ];

  const availabilityOptions = [
    { value: 'weekdays', label: 'Weekdays' },
    { value: 'weekends', label: 'Weekends' },
    { value: 'both', label: 'Both' },
    { value: 'flexible', label: 'Flexible' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error: insertError } = await supabase.from('volunteers').insert({
      user_id: user?.id || null,
      full_name: form.fullName,
      email: form.email,
      phone: form.phone || null,
      program: form.program,
      availability: form.availability,
      message: form.message || null,
    });

    setLoading(false);

    if (insertError) {
      setError(insertError.message);
    } else {
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setForm({ fullName: '', email: '', phone: '', program: '', availability: '', message: '' });
      }, 4000);
    }
  };

  return (
    <div className="overflow-x-hidden">
      <section className="w-full pt-32 pb-20 md:pb-28 bg-warm-50">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <span className="text-brand-600 text-sm font-semibold tracking-wider uppercase mb-4 block">
            Join Our Team
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 max-w-3xl">
            Become a volunteer
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl leading-relaxed">
            Our volunteers are the heart of everything we do. Whether you have an hour
            or a year, your time creates real, lasting change in communities worldwide.
          </p>
        </div>
      </section>

      <section className="w-full py-20 md:py-28 bg-white">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 max-w-6xl mx-auto">
            {/* Benefits */}
            <div className="space-y-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                Why volunteer with us?
              </h2>
              <div className="space-y-6">
                {[
                  { icon: 'ri-hand-heart-line', title: 'Direct Impact', desc: 'See the immediate results of your work in communities that need it most.' },
                  { icon: 'ri-global-line', title: 'Global Community', desc: 'Join 12,000+ volunteers from 40 countries working together for change.' },
                  { icon: 'ri-award-line', title: 'Skill Building', desc: 'Gain hands-on experience in humanitarian work, project management, and leadership.' },
                  { icon: 'ri-time-line', title: 'Flexible Commitment', desc: 'Volunteer for a few hours, a weekend, or join our long-term programs.' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-brand-600 text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div>
              {submitted ? (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-green-600 text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Application Received!</h3>
                  <p className="text-gray-600">We will review your application and contact you within 3 business days.</p>
                  {user && (
                    <p className="text-gray-500 text-sm mt-2">
                      Track your application on your <Link to="/profile" className="text-brand-600 font-semibold">profile page</Link>.
                    </p>
                  )}
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-warm-50 rounded-2xl p-8 space-y-5">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Apply to Volunteer</h3>

                  {error && (
                    <div className="bg-red-50 text-red-600 text-sm p-3 rounded-xl">
                      {error}
                    </div>
                  )}

                  {!user && (
                    <div className="bg-brand-50 border border-brand-200 rounded-xl p-3">
                      <p className="text-brand-800 text-sm">
                        <Link to="/login" className="font-semibold underline">Sign in</Link> to track your application status.
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                    <input
                      type="text"
                      required
                      value={form.fullName}
                      onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none transition-all"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none transition-all"
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none transition-all"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Program Interest</label>
                      <select
                        required
                        value={form.program}
                        onChange={(e) => setForm({ ...form, program: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none transition-all"
                      >
                        <option value="">Select program</option>
                        {programs.map((p) => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Availability</label>
                      <select
                        required
                        value={form.availability}
                        onChange={(e) => setForm({ ...form, availability: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none transition-all"
                      >
                        <option value="">Select availability</option>
                        {availabilityOptions.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tell us about yourself</label>
                    <textarea
                      rows={4}
                      maxLength={500}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none transition-all resize-none"
                      placeholder="Why do you want to volunteer?"
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right">{form.message.length}/500</p>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 bg-gray-900 text-white rounded-full font-semibold hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Submitting...' : 'Submit Application'}
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}